import type { ComicPanel, StudioTool } from "../../comic/comicTypes";

const TOOLS: readonly { tool: StudioTool; label: string; icon: string }[] = [
  { tool: "selectComicObject", label: "Select comic object", icon: "⌖" },
  { tool: "addSpeechBubble", label: "Add speech bubble", icon: "●" },
  { tool: "addThoughtBubble", label: "Add thought bubble", icon: "☁" },
  { tool: "addShoutBubble", label: "Add shout bubble", icon: "✦" },
  { tool: "addNarrationBox", label: "Add narration box", icon: "▭" },
];

interface ComicToolbarProps {
  destructiveAllowed?: boolean;
  panels: ReadonlyArray<ComicPanel>;
  selectedPanelId: string | null;
  tool: StudioTool;
  onToolChange: (tool: StudioTool) => void;
  onPanelChange: (panelId: string) => void;
  onClearPanel: () => void;
  onDuplicatePanel: () => void;
}

export function ComicToolbar({
  panels,
  selectedPanelId,
  tool,
  onToolChange,
  onPanelChange,
  onClearPanel,
  onDuplicatePanel,
  destructiveAllowed = true,
}: ComicToolbarProps) {
  return (
    <section className="comic-toolbar" aria-label="Comic tools">
      <div className="comic-toolbar__tools" role="group" aria-label="Bubble tools">
        {TOOLS.map((item) => (
          <button
            aria-label={item.label}
            aria-pressed={tool === item.tool}
            className={`comic-tool-button${tool === item.tool ? " comic-tool-button--selected" : ""}`}
            key={item.tool}
            onClick={() => onToolChange(item.tool)}
            type="button"
          >
            <span aria-hidden="true">{item.icon}</span>
            {item.label.replace(" comic object", "").replace("Add ", "")}
          </button>
        ))}
      </div>
      <div className="comic-toolbar__panels" role="group" aria-label="Select comic panel">
        <span>Panel</span>
        {panels.map((panel, index) => (
          <button
            aria-label={`Select panel ${index + 1}`}
            aria-pressed={selectedPanelId === panel.id}
            className={
              selectedPanelId === panel.id ? "panel-button panel-button--selected" : "panel-button"
            }
            key={panel.id}
            onClick={() => onPanelChange(panel.id)}
            type="button"
          >
            {index + 1}
          </button>
        ))}
      </div>
      <div className="comic-toolbar__actions">
        <button
          aria-describedby={panels.length <= 1 ? "duplicate-panel-help" : undefined}
          className="comic-action-button"
          disabled={panels.length <= 1}
          onClick={onDuplicatePanel}
          type="button"
        >
          Duplicate panel
        </button>
        <button
          className="comic-action-button comic-action-button--danger"
          disabled={!destructiveAllowed}
          onClick={onClearPanel}
          type="button"
        >
          Clear panel
        </button>
        {panels.length <= 1 ? (
          <span className="sr-only" id="duplicate-panel-help">
            A second panel is required before a panel can be duplicated.
          </span>
        ) : null}
      </div>
    </section>
  );
}
